import {
  LitElement,
  html,
  css,
  unsafeHTML,
  utils,
  paths,
  t,
  broadcastReceiver,
  ref,
  createRef,
} from '@hh/global.js'

class HHModal extends LitElement {
  static properties = {
    id: {},
    open: {
      reflect: true,
    },
    freezeBackground: {
      type: Boolean,
      attribute: 'freeze-background',
    },
  }

  $scrollContainer = createRef()

  constructor() {
    super()
    this.freezeBackground = false
  }

  requestUpdate(name, oldValue) {
    if (name === 'open' && !!this[name]) {
      utils.pauseAllMedia()
    }

    return super.requestUpdate(name, oldValue)
  }

  updated(changedProperties) {
    if (changedProperties.has('open')) {
      if (this.freezeBackground) {
        this.toggleModalLock()
      }
    }
  }

  connectedCallback() {
    super.connectedCallback()

    if (this.freezeBackground) {
      this.open = false
    }

    this.receiver = broadcastReceiver('modal', (...args) => {
      this.openFromDocument(...args)
    })

    this.closeModalHandler = () => {
      this.open = false
      document.body.style.overflowY = ''
    }

    this.addEventListener('close-modal', this.closeModalHandler)
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.receiver?.disconnect()
    if (this.closeModalHandler) {
      this.removeEventListener('close-modal', this.closeModalHandler)
    }
  }

  openFromDocument(data) {
    if (this.id === data.id) {
      this.open = true
    } else {
      this.open = false
      document.body.style.overflowY = ''
      return
    }

    document.body.style.overflowY = 'hidden'

    if (data.element) {
      this.querySelectorAll('[id]').forEach((element) => {
        element.classList.toggle('hidden', element.id !== data.element)
      })
    }
  }

  close(event) {
    if (event.type === 'click') {
      this.open = false
      document.body.style.overflowY = ''

      return
    }
    event.code.toUpperCase() === 'ESCAPE' &&
      (this.open = false && (document.body.style.overflowY = ''))
  }

  toggleModalLock() {
    if (this.open) {
      this.freezeBody()
    } else {
      this.unfreezeBody()
    }
  }

  freezeBody() {
    document.querySelector('html').classList.add('frozen')
    document.querySelector('body').classList.add('frozen')
  }
  unfreezeBody() {
    document.querySelector('html').classList.remove('frozen')
    document.querySelector('body').classList.remove('frozen')
  }

  render() {
    return html` <div class="overlay" @click="${this.close}"></div>
      <div
        @keyup="${this.close}"
        ${ref(this.$scrollContainer)}
        role="dialog"
        aria-modal="true"
        tabindex="-1"
      >
        <button
          @click="${this.close}"
          class="button"
          type="button"
          aria-label="${t('accessibility.close')}"
        >
          ${unsafeHTML(paths.close)}
        </button>
        <div
          class="content"
          role="document"
          aria-label="${t('products.modal.label')}"
          tabindex="0"
        >
          <hh-focus-trap active="${this.open}">
            <slot></slot>
          </hh-focus-trap>
        </div>
      </div>`
  }

  static styles = css`
    :host {
      background-color: var(--hh-background);
      box-sizing: border-box;
      height: 100%;
      left: 0;
      opacity: 0;
      position: fixed;
      top: 0;
      width: 100%;
      visibility: hidden;
      z-index: -1;
      transition: all 0.4s linear;
    }

    :host .overlay {
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      z-index: 1;
      background-color: #2e2a2b80;
    }

    :host([size='popout']) .overlay {
      position: fixed;
    }

    :host([size='flyout']) {
      max-width: var(--hh-max-width, 400px);
      height: auto;
      top: var(--hh-flyout-offset, 30px);
      right: var(--hh-flyout-offset, 30px);
      left: revert;
      border: 0.1rem solid var(--hh-border-color);
    }

    :host([size='popout']) {
      max-width: var(--hh-max-width, 24%);
      height: auto;
      top: 15%;
      right: var(--hh-flyout-offset, 30px);
      left: revert;
      border: 0.1rem solid var(--hh-border-color);
      margin: auto;
      left: 0;
      right: 0;
      padding: var(--hh-padding, 2rem);
    }

    :host([size='popout']) .content {
      position: relative;
      z-index: 3;
      height: auto;
    }

    :host([size='popout']) .button {
      position: absolute;
      z-index: 4;
    }

    :host([size='slideout']) {
      background-color: transparent;
    }

    :host([size='slideout']) [role='dialog'] {
      background-color: var(--hh-color-brand-primary);
      width: 100%;
      max-width: var(--hh-max-width, 102.4rem);
      left: auto;
      right: 0;
      bottom: 0;
      top: 0;
      transform: translateX(100%);
      transition: all var(--hh-duration-long);
      height: 100%;
      overflow-y: auto;
      position: absolute;
      top: 0;
      bottom: 0;
      right: 0;
      z-index: 1;
    }

    @media screen and (min-width: 990px) {
      :host([size='slideout']) {
        top: var(--header-height);
      }
    }

    :host([open='true']) {
      opacity: 1;
      visibility: visible;
      z-index: 1000;
    }

    :host([size='slideout'][open='true']) [role='dialog'] {
      transform: translateX(0%);
    }

    .button {
      width: 3rem;
      height: 3rem;
      position: fixed;
      top: 2rem;
      right: 2rem;
      display: flex;
      background-color: var(--hh-background);
      border: 0.1rem solid var(--hh-border-color);
      border-radius: 50%;
      color: var(--hh-color);
      align-items: center;
      justify-content: center;
      cursor: pointer;
      padding: 0.8rem;
      z-index: 2;
    }

    :host([size='popout']) .button {
      width: 2rem;
      height: 2rem;
      background-color: transparent;
      border: 0;
      padding: 0;
    }

    .button svg {
      width: 100%;
      height: 100%;
    }

    :host([size='flyout']) .button {
      position: absolute;
      top: var(--hh-close-top, 10px);
      right: var(--hh-close-right, 10px);
    }

    :host([size='slideout']) .button {
      color: currentColor;
      background: none;
      width: 1.5rem;
      height: 1.5rem;
      padding: 0;
      border: none;
      border-radius: 0;
      position: absolute;
      margin-top: var(--hh-close-top, 0);
      z-index: 10;
    }

    .content {
      display: flex;
      flex-direction: column;
      height: 100vh;
      overflow: auto;
      width: 100%;
    }

    :host([ctx='product']) .content {
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
    }

    :host([size='flyout']) .content {
      height: auto;
    }

    :host([size='slideout']) .content {
      display: block;
      width: auto;
      height: auto;
      padding: var(--hh-custom-padding, 4.5rem 1.5rem 2rem);
    }

    @media screen and (min-width: 990px) {
      :host([size='slideout']) .content {
        padding: var(--hh-custom-padding, 6rem 7.5rem);
      }
    }

    ::slotted(img) {
      display: block;
      width: 100%;
      height: auto;
    }
  `
}

customElements.define('hh-modal', HHModal)
